import {
  CopropertyDto,
  UpdateCopropertyBuildingsCommand,
  UpdateCopropertyResponse
} from 'api/coproperty';
import { CopropertyBuildingsForm } from 'features/subscription-process/coproperty/CopropertyBuildingsForm';
import { usePanel } from 'hooks/usePanel';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { copropertiesApi } from 'services/swagger-api';
import { useRefetchQuoteData } from '../../../hooks/useRefetchQuoteData';

interface EditCopropertyBuildingsPanelProps {
  /** Copropriété du devis, telle que la contractualisation la renvoie. */
  coproperty: CopropertyDto;
}

/**
 * Sous-panneau « Bâtiments » de la contractualisation MRI : la boucle du parcours,
 * enregistrée ici même. Le verdict d'éligibilité revient avec l'enregistrement ; un refus
 * ne sort pas de la contractualisation, il est annoncé et le panneau reste ouvert pour
 * corriger.
 */
export const EditCopropertyBuildingsPanel: FC<EditCopropertyBuildingsPanelProps> = ({ coproperty }) => {
  const { closePanel, setPanelActions } = usePanel();
  const refetchQuoteData = useRefetchQuoteData();
  const [isSaving, setIsSaving] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const submitRef = useRef<(() => Promise<void>) | null>(null);
  const registerSubmit = useCallback((submit: () => Promise<void>) => {
    submitRef.current = submit;
  }, []);

  const handleSave = useCallback(
    async (command: UpdateCopropertyBuildingsCommand): Promise<UpdateCopropertyResponse | null> => {
      if (!coproperty.id) return null;

      const response = await copropertiesApi.updateCopropertyBuildings({
        copropertyId: coproperty.id,
        updateCopropertyBuildingsCommand: command
      });

      if (!response.isSuccess) {
        toast.error(response.errorMessage || "L'enregistrement des bâtiments a échoué.");
        return null;
      }

      return response;
    },
    [coproperty.id]
  );

  const handleEligibleSaved = useCallback(async () => {
    await refetchQuoteData();
    closePanel();
  }, [refetchQuoteData, closePanel]);

  const handleRefused = useCallback((reasons: string[]) => {
    toast.error(reasons.join(' ') || "Les bâtiments enregistrés ne permettent pas d'assurer cette copropriété.");
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!submitRef.current) return;

    setIsSaving(true);
    try {
      await submitRef.current();
    } finally {
      setIsSaving(false);
    }
  }, []);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'Annuler',
      submitLabel: isSaving ? 'Enregistrement…' : 'Enregistrer',
      submitDisabled: isSaving || !isReady,
      onSubmit: handleSubmit
    });
  }, [setPanelActions, handleSubmit, isSaving, isReady]);

  return (
    <div className="flex flex-col gap-6">
      <CopropertyBuildingsForm
        coproperty={coproperty}
        onSave={handleSave}
        onEligibleSaved={handleEligibleSaved}
        onRefused={handleRefused}
        registerSubmit={registerSubmit}
        onReadyChange={setIsReady}
      />
    </div>
  );
};
